/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { SpinnerContainer, theme, Text } from '@alefeducation/ui-elements-library-core'
import InfiniteScroll from 'react-infinite-scroll-component'
import { FileMagnifyingGlass, InfoCircle } from "@alefeducation/ui-elements-library-icons";
import { Title } from '../../components'
import SearchAndFilter from '../../components/SearchAndFilters'
import {
  DEFAULT_CONTENT_TYPE,
  DEFAULT_PAGE_SETTING,
  ORGANIZATION_QUERY_KEY,
  CONTENT_TYPE_KEYS_ARRAY,
  CONTENT_TYPES_LIST,
  ENTITY_TYPES
} from '../../utils/constants'
import getCentralisedLogs, { getCentralisedLogsResponse, getCentralisedLogsParams } from '../../xhr/getCentralisedLogs'
import { getSelectedOrganization, setSelectedOrganization } from '../../utils/selectedOrganization'
import { LogsBlock, DateBlock } from '../../components/LogBlock'
import AdvancedFilter, { AdvancedFilterProps } from '../../components/AdvancedFilter/AdvancedFilter'
import { transformers } from '../../components/AdvancedFilter/utils'
import { getDateTimeFormat } from '../../utils/dateUtils'
import { Panel } from '../sidePanel'
import getFilters from '../../xhr/getFilters'

const handleUrlUpdate = (params: Record<string, any>) => {
  const url = new URL(location.href)
  url.search = '' // reset all search params
  for (const key in params) {
    const val = params[key]
    if (val && val.toString().length > 0) {
      url.searchParams.set(key, val)
    }
  }
  if (url.search) {
    url.searchParams.set(ORGANIZATION_QUERY_KEY, getSelectedOrganization())
  }
  const newUrl = url.toString()
  if (window.location !== window.parent.location) {
    // in iframe
    window.parent.postMessage(
      {
        source: 'audit-logs-fe',
        location: {
          pathname: url.pathname,
          search: url.search
        }
      },
      window.location.origin
    )
  } else {
    window.history.replaceState(null, '', newUrl)
  }
}

const LogsSection = styled.div`
  border-radius: 10px;
  border: 1px solid ${theme.colors.primary[20]};
  background: ${theme.colors.backgroundLight};
  overflow: auto;
  height: 100%;
  .noLogs {
    text-align: center;
    padding: 24px;
  }
`

const SidePanelWrapper = styled.div`
  overflow-y: auto;
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 378px;
  .side-panel {
    height: 100%;
  }
`

export const FileMagnifyingGlassWrapper = styled.div`
  width: 24px;
  height: 24px;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.primary['20']};
  background-color: ${theme.colors.primary['0']};
  gap: 10px;
  &:hover {
    display: flex !important;
  }
}
`
const DEFAULT_LOGS_DATA = {
  totalRows: 0,
  auditLogs: []
}
type Filter = Pick<getCentralisedLogsParams, 'entityType' | 'searchText'>
type ResponseData = Pick<getCentralisedLogsResponse, 'auditLogs' | 'totalRows'>

type AdvanceFilters = {
  dateRangeFilter: string
  eventTypes: string
  statuses: string
  users: { id: number; uuid: string }[]
}

export default function Home() {
  const params = new URLSearchParams(window.location.search)
  const entityType = params.get('entityType')
    ? params
        .get('entityType')
        .trim()
        .split(',')
    : [DEFAULT_CONTENT_TYPE]

  const [filters, setFilters] = useState<Filter>({
    entityType: (entityType as CONTENT_TYPE_KEYS_ARRAY) || ['ALL'],
    searchText: params.get('searchText') ?? ''
  })

  const [advancedFilterVisibility, setAdvancedFilterVisibility] = useState<boolean>(false)

  const [advancedFilters, setAdvancedFilters] = useState<AdvanceFilters>({
    dateRangeFilter: params.get('dateRangeFilter') || '',
    eventTypes: params.get('eventTypes') || '',
    statuses: params.get('statuses') || '',
    users:
      params
        .get('users')
        ?.split(',')
        ?.map(user => ({ id: 0, uuid: user })) || []
  })

  const [eventFilterList, setEventFilterList] = useState<[{ key: string; value: string }]>([{ key: '', value: '' }])
  const [statusFilterList, setStatusFilterList] = useState<[{ key: string; value: string }]>([{ key: '', value: '' }])

  const [pageOptions, setPageOptions] = useState<PageOptions>({
    pageSize: params.get('pageSize') ? Number(params.get('pageSize')) : DEFAULT_PAGE_SETTING.pageSize,
    pageNumber: params.get('pageNumber') ? Number(params.get('pageNumber')) : DEFAULT_PAGE_SETTING.pageNumber
  })

  const [contentData, setContentData] = useState<ResponseData>(DEFAULT_LOGS_DATA)

  const hasMore = useMemo(() => contentData.auditLogs.length < contentData.totalRows, [contentData])

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const [sidePanelEntity, setSidePanelEntity] = useState<ENTITY_TYPES>()
  const [sidePanelEntityId, setSidePanelEntityId] = useState<string>('')

  const searchLogs = async (filterParams: getCentralisedLogsParams) => {
    setLoading(true)
    setError('')
    await getCentralisedLogs({ ...filterParams })
      .then(res => {
        setContentData(prev => ({
          ...prev,
          totalRows: res.totalRows,
          auditLogs: [...prev.auditLogs, ...res.auditLogs]
        }))
        setPageOptions({ pageNumber: res.request.pageNumber, pageSize: res.request.pageSize })
      })
      .catch(e => console.error('Failed to get content', e))
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    const orgInQuery = params.get(ORGANIZATION_QUERY_KEY)
    if (orgInQuery) {
      setSelectedOrganization(orgInQuery)
    }
  }, [])

  useEffect(() => {
    async function fetchFilters() {
      const listOfFilters = await getFilters(filters.entityType[0] ?? 'ALL')
      setEventFilterList(listOfFilters.eventTypesAndActions)
      setStatusFilterList(listOfFilters.statuses)
    }
    fetchFilters()
  }, [filters.entityType])

  useEffect(() => {
    onFilterOrTextChange()
  }, [
    filters.searchText,
    advancedFilters.dateRangeFilter,
    advancedFilters.eventTypes,
    advancedFilters.statuses,
    advancedFilters.users,
    filters.entityType
  ])

  const onKeywordChange = (s: string) => {
    setFilters(prevState => ({ ...prevState, searchText: s }))
  }

  const onContentTypeChange = (content: getCentralisedLogsParams['entityType']) => {
    setFilters({ entityType: content, searchText: '' })
    setAdvancedFilters({ dateRangeFilter: '', eventTypes: '', statuses: '', users: [] })
  }

  const onFilterOrTextChange = () => {
    setContentData(DEFAULT_LOGS_DATA)
    setPageOptions(DEFAULT_PAGE_SETTING)
    handleUrlUpdate({
      ...filters,
      ...advancedFilters,
      ...{ users: advancedFilters.users.map(user => user.uuid) },
      ...((filters.entityType.length || filters.searchText.length) > 0
        ? DEFAULT_PAGE_SETTING
        : {
            pageNumber: '',
            pageSize: ''
          })
    })
    searchLogs({
      ...filters,
      ...advancedFilters,
      ...DEFAULT_PAGE_SETTING
    })
  }

  const onAdvanceFilterChange = (content: AdvancedFilterProps['content']) => {
    const flatArray: Record<keyof AdvanceFilters, any> = {
      dateRangeFilter: undefined,
      eventTypes: undefined,
      statuses: undefined,
      users: undefined
    }
    content.forEach(f => {
      flatArray[f.key] = transformers[f.type](f.content)
    })
    flatArray.dateRangeFilter = flatArray.dateRangeFilter
      .split(',')
      .map((date: string) => (date ? getDateTimeFormat(date) : ''))
      .join(',')
    flatArray.users = JSON.parse(flatArray.users)
    setAdvancedFilters(flatArray)
    setPageOptions(DEFAULT_PAGE_SETTING)
  }

  const advanceFilterContents: AdvancedFilterProps['content'] = useMemo(() => {
    return [
      {
        title: 'Events',
        type: 'checkbox',
        key: 'eventTypes',
        content:
          eventFilterList &&
          eventFilterList.map(item => ({
            label: item.value,
            value: item.key,
            isChecked: advancedFilters.eventTypes ? advancedFilters.eventTypes.includes(item.key) : false
          }))
      },
      {
        title: 'Status',
        type: 'checkbox',
        key: 'statuses',
        content:
          statusFilterList &&
          statusFilterList.map(item => ({
            label: item.value,
            value: item.key,
            isChecked: advancedFilters.statuses ? advancedFilters.statuses.includes(item.key) : false
          }))
      },
      {
        title: 'User',
        type: 'usersSelector',
        key: 'users',
        content: [
          {
            selectedIds: advancedFilters.users ? advancedFilters.users.map(user => ({ id: user.id || 0, uuid: user.uuid })) : []
          }
        ]
      },
      {
        title: 'Date',
        key: 'dateRangeFilter',
        type: 'datePicker',
        content: [
          {
            value: advancedFilters.dateRangeFilter
              ? advancedFilters.dateRangeFilter.split(',').map(date => (date ? new Date(date) : null))
              : [null, null]
          }
        ]
      }
    ] as AdvancedFilterProps['content']
  }, [advancedFilters.dateRangeFilter, advancedFilters.eventTypes, eventFilterList])

  const closeSidePanel = () => {
    setSidePanelEntity(null)
    setSidePanelEntityId('')
  }

  return (
    <div className="home-app-wrapper">
      <div className="main-layout">
        <SearchAndFilter
          contentTypes={filters.entityType}
          onContentTypeChange={type => onContentTypeChange(type)}
          onSearch={onKeywordChange}
          searchValue={filters.searchText}
          advancedFilter={{
            selected: 0,
            ontoggle: setAdvancedFilterVisibility,
            visible: advancedFilterVisibility
          }}
        />
        <Title />
        <LogsSection id="logs-section-wrapper">
          {!contentData.auditLogs.length && !loading ? (
            <div className="noLogs">
              <Text weight="bold" variant="body-large" color="primary.60">
                No Logs Yet!
              </Text>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={contentData.auditLogs.length}
              style={{
                padding: '24px'
              }}
              next={() => {
                const newPage = pageOptions.pageNumber + 1
                searchLogs({
                  ...filters,
                  ...advancedFilters,
                  pageNumber: newPage,
                  pageSize: pageOptions.pageSize
                })
                setPageOptions({ pageNumber: pageOptions.pageNumber + 1, pageSize: pageOptions.pageSize })
                }
              }
              scrollableTarget="logs-section-wrapper"
              hasMore={hasMore}
              loader={<div />}
            >
              {contentData.auditLogs.map((log, index) => (
                <LogsBlock key={log.auditDate + index} data-testid={log.auditDate + index}>
                  <div className="meta-info-wrapper">
                    <DateBlock date={log.auditDate} />
                    <span className="vertical-separator" />
                    <span className="entity-tag">
                      <Text variant="body-medium" color="primary.100" data-testid={`entity-type-tag-${index}-${log.entityType}`}>
                        {CONTENT_TYPES_LIST[log.entityType] ?? log.entityType}
                      </Text>
                    </span>
                    <FileMagnifyingGlassWrapper className="visible-on-hover cursor border-round">
                      <FileMagnifyingGlass
                        className="visible-on-hover cursor"
                        color={theme.colors.primary[100]}
                        data-testid="side-panel-trigger-file-magnifying-glass"
                        onClick={() => {
                          setSidePanelEntity(log.entityType as ENTITY_TYPES)
                          setSidePanelEntityId(log.entityId)
                        }}
                      />
                    </FileMagnifyingGlassWrapper>
                  </div>
                  <div
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: log.message
                    }}
                  />
                </LogsBlock>
              ))}
            </InfiniteScroll>
          )}
          {loading ? (
            <div style={{ paddingBottom: '24px' }}>
              <SpinnerContainer />
            </div>
          ) : null}
          {error ? (
            <div
              style={{
                textAlign: 'center'
              }}
            >
              <Text variant="body-large" weight="bold" color="error.100">
                {error}
              </Text>
            </div>
          ) : null}
        </LogsSection>
      </div>
      <div
        id="side-panel-place"
        data-testid="side-panel"
        className="side-panel"
        style={{
          display: advancedFilterVisibility ? '' : 'none',
          margin: '-16px -24px -16px 0px'
        }}
      >
        <AdvancedFilter
          content={advanceFilterContents.filter(filterElement =>
            filters.entityType[0] === 'ALL' || filters.entityType[0] === 'CONTENT_REPOSITORY'
              ? filterElement.key !== 'statuses'
              : true)}
          onUpdate={onAdvanceFilterChange}
          onClose={() => setAdvancedFilterVisibility(prev => !prev)}
        />
      </div>
      {sidePanelEntity && sidePanelEntityId ? (
        <SidePanelWrapper>
          <Panel id={sidePanelEntityId} type={sidePanelEntity} onClose={closeSidePanel} panelClass="side-panel" />
        </SidePanelWrapper>
      ) : null}
    </div>
  )
}
